/**
 * Calendar-date helpers. Everything here works on `YYYY-MM-DD` strings and never
 * touches time zones: a booking date is a calendar date, and turning it into a
 * Date object at UTC midnight is the classic source of off-by-one-day bugs.
 *
 * All ranges are half-open, `[arrival, departure)`, matching the schema.
 */

export type ISODate = string

const DAY_MS = 86_400_000

export function toISO(d: Date): ISODate {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Local midnight of the given calendar date. */
export function fromISO(iso: ISODate): Date {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y!, (m ?? 1) - 1, d ?? 1)
}

/**
 * Local midnight for ANY date-carrying string — calendar date or full timestamp.
 *
 * The formatters used to do `new Date(iso + 'T00:00:00')`, which is correct for
 * a calendar date and silently lethal for a timestamp: "…T11:27:03+00:00" plus
 * "T00:00:00" is no date at all, Intl.format then throws a RangeError, and one
 * such value takes the whole view into the error boundary (found 18.08.2026 in
 * TestFlight 1.0(26): a `startbeleg_at` timestamp handed to `formatDate`; Safari
 * words the error "date value is not finite in DateTimeFormat format()").
 *
 * So: take the calendar part, ignore the rest, and return null instead of an
 * invalid Date — a formatter shows an empty string, never a crash.
 */
export function kalendertag(wert: string | null | undefined): Date | null {
  const iso = String(wert ?? '').slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null
  const d = new Date(iso + 'T00:00:00')
  return Number.isFinite(d.getTime()) ? d : null
}

export function todayISO(): ISODate {
  return toISO(new Date())
}

export function addDays(iso: ISODate, days: number): ISODate {
  const d = fromISO(iso)
  d.setDate(d.getDate() + days)
  return toISO(d)
}

export function addMonths(iso: ISODate, months: number): ISODate {
  const d = fromISO(iso)
  const day = d.getDate()
  d.setDate(1)
  d.setMonth(d.getMonth() + months)
  // Clamp: 31 Jan + 1 month is 28/29 Feb, not 2/3 Mar.
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
  d.setDate(Math.min(day, lastDay))
  return toISO(d)
}

/** Number of nights in `[from, to)`. Negative if `to` precedes `from`. */
export function nights(from: ISODate, to: ISODate): number {
  return Math.round((fromISO(to).getTime() - fromISO(from).getTime()) / DAY_MS)
}

/** Every date in `[from, to)`. */
export function eachDate(from: ISODate, to: ISODate): ISODate[] {
  const out: ISODate[] = []
  for (let d = from; d < to; d = addDays(d, 1)) out.push(d)
  return out
}

/** Half-open overlap test — a departure on the same day as an arrival is fine. */
export function overlaps(aFrom: ISODate, aTo: ISODate, bFrom: ISODate, bTo: ISODate): boolean {
  return aFrom < bTo && bFrom < aTo
}

/** Monday = 0 … Sunday = 6, matching the `dow_mask` bit order in the schema. */
export function isoWeekday(iso: ISODate): number {
  return (fromISO(iso).getDay() + 6) % 7
}

export function isWeekend(iso: ISODate): boolean {
  return isoWeekday(iso) >= 5
}

export function monthStart(iso: ISODate): ISODate {
  return iso.slice(0, 8) + '01'
}

export function monthEnd(iso: ISODate): ISODate {
  return addMonths(monthStart(iso), 1)
}

/** Age in whole years on a given date. */
export function ageOn(birthdate: ISODate, on: ISODate): number {
  const b = fromISO(birthdate)
  const d = fromISO(on)
  let age = d.getFullYear() - b.getFullYear()
  const before =
    d.getMonth() < b.getMonth() ||
    (d.getMonth() === b.getMonth() && d.getDate() < b.getDate())
  if (before) age -= 1
  return age
}

/** Clamps `[from, to)` into `[lo, hi)`; returns null when they do not overlap. */
export function clampRange(
  from: ISODate, to: ISODate, lo: ISODate, hi: ISODate,
): [ISODate, ISODate] | null {
  const a = from > lo ? from : lo
  const b = to < hi ? to : hi
  return a < b ? [a, b] : null
}

/**
 * Whole days from today until `iso`, counted on the calendar rather than the clock.
 *
 * A trial that ends on 25 August is "30 days" on the 26th of July and "29 days" on
 * the 27th — which is how a person counts it. Measuring elapsed milliseconds and
 * rounding is what produces "30 days" when 29 days and 8 hours remain, or "29" one
 * minute after signing up for a 30-day trial. Both are technically defensible and
 * both look like a mistake.
 */
export function daysUntil(iso: string): number {
  const end = new Date(iso)
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate())
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.round((endDay.getTime() - today.getTime()) / 86_400_000)
}
